import { Button, Chip, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { DRAWER_MAX_SELECTION_COUNT } from "app/appConstants";
import { AxiosResponse, GenericAbortSignal } from "axios";
import { MprcTypography } from "components";
import { IconButtonPreview } from "components/iconButtons/IconButtonPreview";
import { PublicationSelectorDto, PublicationSelectorDtoPaginatedList } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";
import { IContentHeaderProps } from "views/common/GenericListDrawer/GenericListDrawer";
import { PublicationViewerDialog } from "views/knowledge/publication/dialogs/PublicationViewerDialog";

////////////////////////////////////////////////////////////////////////////////

type ItemData = GenericListItemsType<PublicationSelectorDto>;

////////////////////////////////////////////////////////////////////////////////

export type PublicationSelectorFilterOptions = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
    signal?: AbortSignal;
};

export type PublicationSelectorProps = {
    title?: string;
    subtitle?: string;
    multi?: boolean;
    showCounter?: (data: IContentHeaderProps) => boolean;
    contentHeader?: (data: IContentHeaderProps) => React.ReactNode;
    onClose: (publications: PublicationSelectorDto[] | undefined) => void;
};

type IProps = PublicationSelectorProps & {
    onGetData: (options: PublicationSelectorFilterOptions) => Promise<AxiosResponse<PublicationSelectorDtoPaginatedList>>;
};

/////////////////////////////////////////////////////////////////////////////////

export const BasePublicationSelectorDrawer: React.FC<IProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation(["common", "knowledge"]);

    const { title, subtitle, multi = false, showCounter, contentHeader, onGetData, onClose } = props;

    const [selected, setSelection] = useState<PublicationSelectorDto[]>([]);
    const [dialog, setDialog] = useState<any>();

    ////////////////////////////////////////////////////////////////////////////////

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<PublicationSelectorDtoPaginatedList> => {
        return new Promise((resolve, reject) => {
            onGetData({
                ...genericFilter,
                signal: signal as AbortSignal
            })
                .then((response) => resolve(response.data))
                .catch(reject);
        });
    };

    // Ao clicar em visualizar uma publicação.
    const onPreviewClick = (item: PublicationSelectorDto) => {
        setDialog(<PublicationViewerDialog
            publicationId={item.publicationId}
            onClose={() => setDialog(null)}
        />);
    };


    /**
     * Renderiza uma linha da Lista.
     * @param props
     */
    function renderRow(props: ListChildComponentProps<ItemData>) {
        const { index, style } = props;
        const { items, onViewerClick } = props.data;

        const item = items[index];
        if (item == undefined) return <></>;

        return (
            <div key={index} style={style}>
                <ListItem
                    disablePadding
                    disableGutters
                    dense
                    secondaryAction={
                        <IconButtonPreview color="muted" onClick={() => onPreviewClick(item)} size="small" />
                    }
                >
                    <ListItemButton disableGutters selected={item.selected} onClick={() => onViewerClick && onViewerClick(item)} sx={{ height: style.height }}>
                        <ListItemText title={item.title}>
                            <MprcTypography noWrapRows={2} variant="h6">{item.title}</MprcTypography>
                        </ListItemText>
                    </ListItemButton>
                </ListItem>
            </div>
        );
    }

    // Ao selecionar um item da lista.
    const onItemClick = (item: PublicationSelectorDto) => {
        if (!multi)
            onClose([item]);
        else {
            if (selected.find(p => p.publicationId == item.publicationId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("Selector.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT, ns: "shared" }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("Selector.AlreadySelectedMessage", { name: item.title, ns: "shared" }), { variant: "error" });
            }
        }
    };


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <GenericListDrawer
            title={title || t("knowledge:Publication.PublicationSelectorDrawer.Title")}
            subtitle={subtitle}
            itemHeight={44}
            onClose={() => onClose(undefined)}
            onGetData={handleGetData}
            onViewerClick={onItemClick}
            emptyDataMessage={t("knowledge:Publication.PublicationSelectorDrawer.ListEmptyMessage")}
            emptyFilterMessage={t("knowledge:Publication.PublicationSelectorDrawer.NoRecordsFoundText")}
            renderRow={renderRow}
            selection="normal"
            contentHeader={contentHeader}
            showCounter={showCounter}
            footer={
                <div style={{ width: "100%" }}>

                    {dialog}

                    {
                        multi && selected.length > 0 &&
                        <>
                            <div style={{ maxHeight: 200, overflow: "auto" }}>
                                <p><small>{t("Selector.SelectedText", { ns: "shared" })}:</small></p>
                                {
                                    selected.map((item, index) => (
                                        <Chip
                                            key={index}
                                            label={item.title}
                                            size="small"
                                            sx={{ mb: 0.5, mr: 0.5 }}
                                            onDelete={() =>
                                                setSelection(selected.filter(p => p.publicationId != item.publicationId))
                                            }
                                        />
                                    ))
                                }
                            </div>
                            <hr />
                            <div style={{ textAlign: "center" }}>
                                <Button
                                    variant="contained"
                                    onClick={() => onClose(selected)}
                                >
                                    {t("Selector.AddSelectedButtonText", { ns: "shared" })}
                                </Button >
                            </div>
                        </>
                    }
                </div>
            }
        />
    );
};
